import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Avatar,
  Button,
  Grid2,
  Skeleton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Header from "../../Header/Header";
import Footer from "../../Footer/Footer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import HomeIcon from "@mui/icons-material/Home";
import Slider from "react-slick"; // Assuming you want to use react-slick for image slider
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLoader } from "../../loader/loader-context";
import PropertyCard from "../card";
import { db } from "../../../firestore-DB-config/firebase-config";
import { getDocs, collection } from "firebase/firestore";

interface PropertyCardProps {
  id: string;
  image: string;
  price: string;
  title: string;
  address: string;
  beds: number;
  baths: number;
  description: string;
  label: string;
  labelColor: string;
  labelIcon: JSX.Element;
  roomDetails: string;
  postedBy: string;
  postedDate: string;
  type: string;
  images: string[];
  landmark: string;
  floor: number;
  facing: string;
  parking: string;
  gatedSecurity: string;
  area: string;
  balcony: number;
  age: string;
  furnished: string;
  amenities: string[];
}

function PropertyDetails() {
  //Fetch data from firestore
  const [properties, setProperties] = useState<PropertyCardProps[]>([]);
  const { showLoader, hideLoader } = useLoader();
  const [loading, setLoading] = useState(true); // Add loading state

  // Function to map labels to icons
  const getLabelIcon = (label: string): JSX.Element => {
    switch (label) {
      case "Newest":
        return <HomeIcon />;
      case "Discounted":
        return <AttachMoneyIcon />;
      case "Popular":
        return <WhatshotIcon />;
      default:
        return <HomeIcon />; // Default to HomeIcon if no match
    }
  };

  // Define a function to map labels to color styles
  const getLabelColor = (label: string): string => {
    switch (label) {
      case "Newest":
        return "#1976d2"; // Blue for 'Newest'
      case "Discounted":
        return "#2e7d32"; // Green for 'Discounted'
      case "Popular":
        return "#d32f2f"; // Red for 'Popular'
      default:
        return "#555555"; // Default color for unknown labels
    }
  };

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        showLoader();
        const querySnapshot = await getDocs(collection(db, "properties"));
        const propertiesData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            image: data.imageUrls,
            price: data.price,
            title: data.title,
            address: data.address,
            beds: data.beds,
            baths: data.baths,
            label: data.label,
            labelColor: data.labelColor,
            labelIcon: getLabelIcon(data.label),
            roomDetails: data.roomDetails,
            postedBy: data.postedBy,
            postedDate: data.postedDate,
            type: data.type,
            images: data.imageUrls || [], // Provide default empty array
            description: data.description,
            landmark: data.landmark,
            floor: data.floor,
            facing: data.facing,
            parking: data.parking,
            gatedSecurity: data.gatedSecurity,
            area: data.area,
            balcony: data.balcony,
            age: data.age,
            furnished: data.furnished,
            amenities: data.amenities,
          } as PropertyCardProps;
        });
        setProperties(propertiesData);
      } catch (error) {
        console.error("Error fetching properties:", error); // Log the error
      } finally {
        hideLoader();
        setLoading(false); // Set loading to false after data is fetched
      }
    };

    fetchProperties();
  }, []);

  const { id } = useParams(); // Get the property ID from the URL
  const property = properties.find((p) => p.id === id); // Find the property by ID

  // Function to get related properties based on type and label
  const getRelatedProperties = (
    propertyType: string,
    propertyLabel: string,
    excludeId: string,
    count: number
  ) => {
    // Filter properties that match the type or label and exclude the current property
    const filteredProperties = properties.filter(
      (p) =>
        p.id !== excludeId &&
        (p.type === propertyType || p.label === propertyLabel)
    );

    // Shuffle the filtered properties and return the specified count
    const shuffled = filteredProperties.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count); // Return the first `count` number of related properties
  };

  if (loading) {
    return (
      <>
        <Header />
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ my: 6 }}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="80%" height={30} />
            <Skeleton variant="text" width="90%" height={30} />
            <Skeleton variant="text" width="90%" height={30} />
          </Box>
        ))}
        <Footer />
      </>
    );
  }

  if (!property) {
    return (
      <>
        <Header />
        <Typography variant="h6">Property not found.</Typography>
        <Footer />
      </>
    );
  }

  const relatedProperties = getRelatedProperties(
    property.type,
    property.label,
    property.id,
    3
  );

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  interface DetailItemProps {
    label: string;
    value: string | number | boolean;
  }

  const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
    <Typography variant="body1" sx={{ fontSize: "1.25rem", mb: 1.5 }}>
      <Typography
        component="span"
        sx={{ fontWeight: "550", fontSize: "1.25rem" }}
      >
        {label}
      </Typography>{" "}
      <Typography component="span" sx={{ color: "gray", fontSize: "1.25rem" }}>
        {value}
      </Typography>
    </Typography>
  );

  return (
    <>
      <Header />
      <Box
        sx={{
          maxWidth: { xs: "100%", sm: "95%" }, // Full width on small screens
          margin: { xs: "20px auto", md: "40px auto" }, // Less margin on smaller screens
          padding: { xs: "10px", sm: "20px" },
          backgroundColor: "white",
        }}
      >
        <IconButton onClick={() => window.history.back()} sx={{ mb: 2 }}>
          <ArrowBackIcon />
          <Typography sx={{ ml: 1 }}>Back</Typography>
        </IconButton>

        <Box sx={{ p: 3 }}>
          {/* Property Title and Price */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }} // Align vertically on small screens
            flexDirection={{ xs: "column", md: "row" }} // Stack title and price on small screens
            mb={2}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              mr={{ xs: 0, md: 18 }}
              mb={{ xs: 2, md: 0 }}
            >
              {property.title} | {property.address}
            </Typography>
            <Typography variant="h4" color="textSecondary" fontWeight="bold">
              {property.price}
            </Typography>
          </Box>

          {/* Owner Tags */}
          <Box
            display="flex"
            alignItems="center"
            mb={2}
            flexDirection="row" // Stack on extra small screens
            gap={{ xs: 0.5, sm: 2 }}
          >
            <Avatar sx={{ width: 40, height: 40, mr: 1 }}>T</Avatar>
            <Typography>{property.postedBy}</Typography>
            <Typography sx={{ ml: 2, color: "gray" }}>
              {property.postedDate}
            </Typography>
          </Box>

          {/* Tags */}
          <Box
            display="flex"
            justifyContent={{ xs: "flex-start", md: "space-between" }} // On small screens, align tags and button to the left
            flexDirection={{ xs: "column", sm: "row" }} // Stack tags and button on small screens, row on medium and larger
            alignItems={{ xs: "flex-start", md: "center" }} // Align items properly across breakpoints
            gap={{ xs: 2, sm: 0 }} // Add gap between items on small screens
            mb={3}
          >
            {/* Tags Section */}
            <Box
              display="flex"
              gap={2}
              flexWrap="wrap" // Ensure tags wrap properly on smaller screens
              mb={{ xs: 2, md: 0 }} // Add bottom margin for small screens to separate tags from button
            >
              <Box
                sx={{
                  backgroundColor: "#011D33",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "14px",
                  display: "flex",
                  alignItems: "center",
                  fontSize: { xs: "0.8rem", md: "0.9rem" }, // Adjust font size based on screen size
                }}
              >
                {property.type}
              </Box>
              <Chip
                label={property.label}
                icon={property.labelIcon}
                sx={{
                  backgroundColor: "white",
                  color: getLabelColor(property.label),
                  fontWeight: "bold",
                  fontSize: { xs: "0.8rem", md: "1rem" }, // Adjust font size
                  border: `2px solid ${getLabelColor(property.label)}`, // Custom border with dynamic color
                  "& .MuiChip-icon": {
                    color: getLabelColor(property.label),
                  },
                }}
              />
            </Box>

            {/* Contact Button */}
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#011D33",
                width: { xs: "100%", sm: "auto" }, // Full-width button on small screens
              }}
            >
              Contact Owner
            </Button>
          </Box>

          {/* Image Slider */}
          <Box sx={{ mb: 3, mt: 6 }}>
            <Slider {...sliderSettings}>
              {property.images.map((img, index) => (
                <Box
                  key={index}
                  component="img"
                  src={img}
                  alt="Property"
                  sx={{
                    maxWidth: "100%",
                    height: { xs: "300px", sm: "400px", md: "700px" }, // Responsive height
                    objectFit: "cover", // Ensure the image covers the area without stretching
                    borderRadius: "12px",
                  }}
                />
              ))}
            </Slider>
          </Box>

          {/* Property Details */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: "500", mb: 2 }}>
              Details
            </Typography>

            <Typography
              variant="body1"
              fontWeight={400}
              sx={{ fontSize: "1.20rem", mb: 1 }}
            >
              <Typography
                component="span"
                sx={{ fontWeight: "550", fontSize: "1.25rem" }}
              >
                Location :
              </Typography>{" "}
              {property.address}
            </Typography>

            <Typography
              variant="body1"
              fontWeight={400}
              sx={{ fontSize: "1.20rem", mb: 1 }}
            >
              <Typography
                component="span"
                sx={{ fontWeight: "550", fontSize: "1.25rem" }}
              >
                Landmark :
              </Typography>{" "}
              {property.landmark}
            </Typography>

            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection={{ xs: "column", sm: "row" }} // Stack vertically on small screens
            >
              {/* Column 1 */}
              <Box flex="1" sx={{ pr: { xs: 0, md: 20 } }}>
                {" "}
                {/* Adjust spacing */}
                {[
                  { label: "Floor:", value: property.floor },
                  { label: "Bedrooms:", value: property.beds },
                  { label: "Gated Security:", value: property.gatedSecurity },
                  { label: "Age of Property:", value: property.age },
                ].map((detail, index) => (
                  <DetailItem
                    key={index}
                    label={detail.label}
                    value={detail.value}
                  />
                ))}
              </Box>

              {/* Column 2 */}
              <Box flex="1" sx={{ pr: { xs: 0, md: 20 } }}>
                {[
                  { label: "Facing:", value: property.facing },
                  { label: "Bathrooms:", value: property.baths },
                  { label: "Area:", value: property.area },
                  { label: "Furnished:", value: property.furnished },
                ].map((detail, index) => (
                  <DetailItem
                    key={index}
                    label={detail.label}
                    value={detail.value}
                  />
                ))}
              </Box>

              {/* Column 3 */}
              <Box flex="1">
                {[
                  { label: "Property Type:", value: property.type },
                  { label: "Price Range:", value: property.price },
                  { label: "Parking:", value: property.parking },
                  { label: "Balconies:", value: property.balcony },
                ].map((detail, index) => (
                  <DetailItem
                    key={index}
                    label={detail.label}
                    value={detail.value}
                  />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Description */}
          <Box sx={{ mt: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: "500", mb: 2 }}>
              Description:
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ fontSize: { xs: "15px", sm: "16px" } }}
              lineHeight={1.7}
            >
              {/* Render the description using dangerouslySetInnerHTML to parse the HTML */}
              <span
                dangerouslySetInnerHTML={{ __html: property.description }}
              />
            </Typography>
          </Box>

          {/* Amenities Section */}
          <Box sx={{ mt: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: "600", mb: 3 }}>
              Amenities:
            </Typography>

            <Grid2 container spacing={2}>
              {property.amenities.map((amenity, index) => (
                <Grid2
                  key={index}
                  sx={{
                    width: { xs: "100%", sm: "46%", md: "31%" }, // Responsive width for different screen sizes
                    mb: 1, // Bottom margin between rows
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Avatar
                      sx={{
                        bgcolor: "#004368", // Dark blue background for the number
                        color: "white",
                        width: 32,
                        height: 32,
                        fontSize: "1rem",
                        marginRight: 2,
                      }}
                    >
                      {index + 1}
                    </Avatar>
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "1.1rem", color: "gray" }}
                    >
                      {amenity}
                    </Typography>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Related Properties Section */}
          <Box sx={{ mt: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: "600", mb: 3 }}>
              Related Properties:
            </Typography>
            <Grid2
              container
              spacing={{ xs: 4, sm: 8, md: 10 }}
              justifyContent={{ xs: "center", md: "left" }}
            >
              {relatedProperties.map((relatedProperty) => (
                <Grid2
                  key={relatedProperty.id}
                  size={{ xs: 10, md: 6, lg: 4 }}
                >
                  <PropertyCard
                    prp={{
                      id: relatedProperty.id,
                      image: relatedProperty.image,
                      price: relatedProperty.price,
                      title: relatedProperty.title,
                      address: relatedProperty.address,
                      beds: relatedProperty.beds,
                      baths: relatedProperty.baths,
                      label: relatedProperty.label,
                      labelColor: relatedProperty.labelColor as
                        | "error"
                        | "primary"
                        | "success",
                      labelIcon: relatedProperty.labelIcon,
                      type: relatedProperty.type,
                    }}
                  />
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  );
}

export default PropertyDetails;
